import { ArrowLeft, ArrowRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PersonaWorkshopSession, PersonaData, JtbdData, ValuePropositionCanvas } from '@ispot/shared'
import { migratePersonaData } from '@ispot/shared'

export function isJtbdComplete(jtbd: JtbdData): boolean {
  return jtbd.pains.length >= 2 && jtbd.gains.length >= 1 && !!jtbd.customerProblem?.statement
}

export function getJtbdReadiness(personaData: PersonaData): { ready: number; total: number } {
  const jtbds = personaData.jtbds || []
  return { ready: jtbds.filter(isJtbdComplete).length, total: jtbds.length }
}

interface StepperProps {
  currentStep: 'select-persona' | 'select-jtbd'
}

export function Stepper({ currentStep }: StepperProps) {
  const steps = [
    { id: 'select-persona', label: 'Select persona', done: currentStep === 'select-jtbd' },
    { id: 'select-jtbd', label: 'Select job to be done', done: false },
  ]

  return (
    <div className="flex items-center gap-6 mb-8">
      {steps.map((s, i) => (
        <div key={s.id} className="flex items-center gap-2">
          <div className={cn(
            'w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium',
            s.done ? 'bg-[var(--color-market)] text-white' :
            s.id === currentStep ? 'bg-[var(--color-warm-800)] text-white' :
            'bg-[var(--color-warm-200)] text-[var(--color-text-muted)]'
          )}>
            {s.done ? <Check className="w-3.5 h-3.5" /> : i + 1}
          </div>
          <span className={cn(
            'text-sm font-medium',
            s.id === currentStep ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted)]'
          )}>
            {s.label}
          </span>
          {i < steps.length - 1 && <div className="w-12 h-px bg-[var(--color-border)]" />}
        </div>
      ))}
    </div>
  )
}

interface PersonaStepProps {
  sessions: PersonaWorkshopSession[]
  selectedSession: PersonaWorkshopSession | null
  onSelect: (session: PersonaWorkshopSession) => void
  onBack: () => void
  onNext: () => void
}

export function PersonaStep({ sessions, selectedSession, onSelect, onBack, onNext }: PersonaStepProps) {
  // Show completed personas AND active ones that have a name and at least one JTBD
  const completed = sessions.filter(s => {
    if (s.status === 'completed') return true
    if (s.status === 'active') {
      const pd = migratePersonaData(s.personaData as PersonaData)
      return !!pd.name && (pd.jtbds?.length ?? 0) > 0
    }
    return false
  })

  return (
    <>
      <p className="text-xs font-medium uppercase tracking-wider text-[var(--color-text-muted)] mb-4">
        Select a persona to create a VPC for
      </p>
      <div className="space-y-3">
        {completed.map(session => {
          const pd = migratePersonaData(session.personaData as PersonaData)
          const readiness = getJtbdReadiness(pd)
          const isSelected = selectedSession?.id === session.id
          return (
            <button key={session.id} onClick={() => onSelect(session)} className={cn(
              'w-full text-left p-4 rounded-lg border transition-colors',
              isSelected ? 'border-[var(--color-market)] bg-[var(--color-market)]/5' : 'border-[var(--color-border)] hover:border-[var(--color-border-strong)]'
            )}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[var(--color-market)] text-white flex items-center justify-center text-sm font-medium shrink-0">
                  {pd.name?.slice(0, 2) || '??'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-[var(--color-text)]">{pd.name || 'Unnamed'} — {pd.role || 'No role'}</div>
                  <div className="text-xs text-[var(--color-text-muted)] mt-0.5">{pd.archetype ? `"${pd.archetype}"` : ''} · {readiness.total} job{readiness.total !== 1 ? 's' : ''}</div>
                </div>
                <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', readiness.ready > 0 ? 'bg-[var(--color-market)]/10 text-[var(--color-market)]' : 'bg-[var(--color-warm-200)] text-[var(--color-text-muted)]')}>
                  {readiness.ready}/{readiness.total} ready
                </span>
              </div>
            </button>
          )
        })}
        {completed.length === 0 && (
          <p className="text-sm text-[var(--color-text-muted)] py-8 text-center">No personas with jobs to be done yet. Create a persona with at least one JTBD in the Persona workshop first.</p>
        )}
      </div>
      <div className="flex justify-between mt-8">
        <button onClick={onBack} className="flex items-center gap-1.5 px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors"><ArrowLeft className="w-4 h-4" /> Back</button>
        <button onClick={onNext} disabled={!selectedSession} className={cn('flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors', selectedSession ? 'bg-[var(--color-market)] text-white hover:opacity-90' : 'bg-[var(--color-warm-200)] text-[var(--color-text-muted)] cursor-not-allowed')}>
          Next: select job <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </>
  )
}

interface JtbdStepProps {
  session: PersonaWorkshopSession
  vpcs: ValuePropositionCanvas[]
  selectedJtbd: JtbdData | null
  onSelect: (jtbd: JtbdData) => void
  onBack: () => void
  onCreate: () => void
}

export function JtbdStep({ session, vpcs, selectedJtbd, onSelect, onBack, onCreate }: JtbdStepProps) {
  const pd = migratePersonaData(session.personaData as PersonaData)

  return (
    <>
      <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--color-warm-100)] mb-6">
        <div className="w-7 h-7 rounded-full bg-[var(--color-market)] text-white flex items-center justify-center text-xs font-medium">{(pd.name || '??').slice(0, 2)}</div>
        <span className="text-sm font-medium text-[var(--color-text)]">{pd.name}</span>
        <span className="text-xs text-[var(--color-text-muted)]">{pd.role}</span>
        <button onClick={onBack} className="ml-auto text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]"><ArrowLeft className="w-3 h-3 inline mr-1" />Change</button>
      </div>

      <p className="text-xs font-medium uppercase tracking-wider text-[var(--color-text-muted)] mb-4">Select a job to be done for this VPC</p>

      <div className="space-y-3">
        {(pd.jtbds || []).map(jtbd => {
          const complete = isJtbdComplete(jtbd)
          const existingVpc = vpcs.find(v => v.personaSessionId === session.id && v.jtbdId === jtbd.id)
          const isSelected = selectedJtbd?.id === jtbd.id
          const disabled = !complete || !!existingVpc

          return (
            <button key={jtbd.id} onClick={() => { if (!disabled) onSelect(jtbd) }} disabled={disabled} className={cn(
              'w-full text-left p-4 rounded-lg border transition-colors',
              disabled ? 'opacity-50 cursor-not-allowed border-[var(--color-border)]' : isSelected ? 'border-[var(--color-market)] bg-[var(--color-market)]/5' : 'border-[var(--color-border)] hover:border-[var(--color-border-strong)]'
            )}>
              <div className="flex items-center gap-3">
                <div className={cn('w-5 h-5 rounded-full flex items-center justify-center shrink-0', complete ? 'bg-[var(--color-market)]/10 text-[var(--color-market)]' : 'bg-[var(--color-warm-200)]')}>
                  {complete && <Check className="w-3 h-3" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-[var(--color-text)]">{jtbd.title}</div>
                  <div className="text-xs text-[var(--color-text-muted)] mt-0.5">
                    {jtbd.pains.length} pain{jtbd.pains.length !== 1 ? 's' : ''} · {jtbd.gains.length} gain{jtbd.gains.length !== 1 ? 's' : ''}
                    {!complete && <span className="ml-2 text-amber-600">{jtbd.pains.length < 2 && 'Needs 2+ pains. '}{jtbd.gains.length < 1 && 'Needs 1+ gain. '}{!jtbd.customerProblem?.statement && 'Needs customer problem.'}</span>}
                    {existingVpc && <span className="ml-2 text-[var(--color-text-muted)]">VPC already exists</span>}
                  </div>
                </div>
                <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', existingVpc ? 'bg-[var(--color-warm-200)] text-[var(--color-text-muted)]' : complete ? 'bg-[var(--color-market)]/10 text-[var(--color-market)]' : 'bg-amber-50 text-amber-600')}>
                  {existingVpc ? 'Has VPC' : complete ? 'Ready' : 'Incomplete'}
                </span>
              </div>
            </button>
          )
        })}
      </div>

      <div className="flex justify-between mt-8">
        <button onClick={onBack} className="flex items-center gap-1.5 px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors"><ArrowLeft className="w-4 h-4" /> Back</button>
        <button onClick={onCreate} disabled={!selectedJtbd} className={cn('flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors', selectedJtbd ? 'bg-[var(--color-market)] text-white hover:opacity-90' : 'bg-[var(--color-warm-200)] text-[var(--color-text-muted)] cursor-not-allowed')}>
          Create VPC <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </>
  )
}
